import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import { KizenTypography } from 'app/typography';
import Expander from 'components/Containers/Expander';
import { useTruncationTooltip } from 'components/Kizen/Tooltip';
import { CollapsedButton } from './styles';
import { MarkdownOverlay } from 'components/WYSIWYG/MarkdownEditor/components/MarkdownOverlay';
import { MarkdownEditor } from 'components/WYSIWYG/MarkdownEditor';
import { getHTMLValue } from 'components/WYSIWYG/MarkdownEditor/helpers';
import {
  PREVIEW_LENGTH,
  PREVIEW_LENGTH_OFFSET,
} from 'components/WYSIWYG/MarkdownEditor/constants';
import { markdownPreviewStyles } from 'components/WYSIWYG/MarkdownEditor/styles';

const StyledExpander = styled(Expander)`
  margin-bottom: ${({ hasCollapsedButton }) => (hasCollapsedButton ? 19 : 0)}px;
`;

const ExpandableText = styled(KizenTypography)`
  white-space: pre-wrap;
  overflow-wrap: break-word;
  ${({ expanded }) => {
    if (expanded) {
      return css`
        & p:not(:empty) {
          height: unset !important;
        }
        display: block;
      `;
    }

    return css`
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      max-height: calc(
        1.5em * 3
      ); // 3 lines tall; Fallback in case line-clamp isn't supported because i line is 1.5em right now
    `;
  }};
`;

const CollapsedButtonWrapper = styled.div`
  position: absolute;
  left: 0;
  display: flex;
  gap: 10px;
`;

const StyledMarkdownPreview = styled.div`
  & * {
    font-size: 14px;
  }
  ${markdownPreviewStyles}
`;

export default function LongTextEllipsis({
  children,
  isExpandable = false,
  markdown = null,
  showMarkdownPreview = true,
  ...others
}) {
  const expanderHandle = useRef();
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const [showMarkdown, setShowMarkdown] = useState(false);
  const textRef = useRef();
  const [tooltipProps, tooltip] = useTruncationTooltip({}, 'y');
  const [markdownHTML, setMarkdownHTML] = useState('');

  const toggleExpanded = useCallback(() => {
    setIsExpanded((x) => !x);
  }, []);

  useEffect(() => {
    const convertAction = async () => {
      const html =
        markdown && showMarkdownPreview
          ? await getHTMLValue(
              markdown.slice(0, PREVIEW_LENGTH + PREVIEW_LENGTH_OFFSET),
              true,
              {
                truncate: {
                  characterLimit: PREVIEW_LENGTH,
                  showEllipsis: true,
                },
                condensed: true,
              }
            )
          : '';
      setMarkdownHTML(html);
    };
    convertAction();
  }, [markdown, showMarkdownPreview]);

  useEffect(() => {
    if (isExpandable) {
      const observeOverflow = () => {
        if (textRef.current && !isExpanded) {
          setIsOverflowed(
            textRef.current.scrollHeight > textRef.current.offsetHeight
          );
        }
      };

      observeOverflow();
      window.addEventListener('resize', observeOverflow);
      return () => {
        window.removeEventListener('resize', observeOverflow);
      };
    }
  }, [isExpandable, isExpanded, markdownHTML]);

  const targetRef = useRef(null);

  const handleOpen = () => setShowMarkdown(true);
  const handleClose = () => setShowMarkdown(false);

  return (
    <div>
      {(isExpandable && isOverflowed) || markdown ? (
        <>
          <StyledExpander
            ref={expanderHandle}
            closedState="partially-open"
            showArrowBtn={false}
            gradient={null}
            partiallyOpenHeight={isExpandable && isOverflowed ? 50 : 20}
            hasCollapsedButton
            onChange={toggleExpanded}
          >
            <ExpandableText
              ref={textRef}
              expanded={isExpanded}
              {...(!isExpandable && tooltipProps)}
              {...others}
            >
              {markdownHTML ? (
                <StyledMarkdownPreview
                  dangerouslySetInnerHTML={{ __html: markdownHTML }}
                />
              ) : (
                children
              )}
            </ExpandableText>
          </StyledExpander>
          <CollapsedButtonWrapper>
            {isExpandable && isOverflowed ? (
              <CollapsedButton
                variant="text"
                color="blue"
                onClick={() => {
                  expanderHandle.current.toggle();
                }}
              >
                {isExpanded ? t('Hide Full Message') : t('View Full Message')}
              </CollapsedButton>
            ) : null}
            {markdown ? (
              <>
                <CollapsedButton
                  ref={targetRef}
                  variant="text"
                  color="blue"
                  onClick={handleOpen}
                >
                  {t('View Formatted Text')}
                </CollapsedButton>
              </>
            ) : null}
          </CollapsedButtonWrapper>
        </>
      ) : (
        <>
          <ExpandableText
            ref={textRef}
            expanded={isExpanded}
            {...(!isExpandable && tooltipProps)}
            {...others}
          >
            {children}
          </ExpandableText>
        </>
      )}
      {!isExpandable && tooltip}
      {markdown ? (
        <MarkdownOverlay
          show={showMarkdown}
          onHide={handleClose}
          targetRef={targetRef}
        >
          <MarkdownEditor isMarkdown initialValue={markdown} readonly />
        </MarkdownOverlay>
      ) : null}
    </div>
  );
}
